import React, { useEffect, useRef, useCallback, useMemo } from "react";
import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { StoryAPI } from "../lib/api";
import StoryGrid from "../components/stories/StoryGrid";
import SearchAndFilter from "../components/stories/SearchAndFilter";
import { Loader2 } from "lucide-react";

interface FilterOptions {
  type?: string;
  region?: string;
  context?: string;
  sponsored?: boolean;
}

interface SponsorInfo {
  name: string;
}

interface Story {
  _id: string;
  title: string;
  short_description: string;
  medium_description: string;
  long_description: string;
  type: string;
  context: string;
  city: string;
  region: string;
  is_featured: boolean;
  is_sponsored: boolean;
  sponsor_id?: SponsorInfo;
  downloads: number;
  date_added: string;
  thumbnail_image?: string;
}

interface StoriesResponse {
  stories: Story[];
  total: number;
  pages: number;
  currentPage: number;
}

// Create stable object references to avoid unnecessary re-renders
const EMPTY_FILTERS = {};

const StoriesPage = () => {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<FilterOptions>(EMPTY_FILTERS);
  const [sort, setSort] = useState("downloads");
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Convert filters to string for stable identity in deps array
  const filtersString = useMemo(() => JSON.stringify(filters), [filters]);

  // Fetch stories with infinite scroll
  const { 
    data, 
    fetchNextPage, 
    hasNextPage, 
    isFetchingNextPage, 
    isLoading 
  } = useInfiniteQuery<StoriesResponse>({
    queryKey: ["stories", search, filtersString, sort],
    queryFn: ({ pageParam }) => {
      // Need to parse the filters back to an object
      const parsedFilters = JSON.parse(filtersString);
      
      return StoryAPI.getAll({
        page: pageParam,
        limit: 16,
        search,
        sort,
        is_featured: false,
        ...parsedFilters,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.currentPage < lastPage.pages
        ? lastPage.currentPage + 1
        : undefined,
    staleTime: 1000 * 60 * 5, // Data is fresh for 5 minutes
    gcTime: 1000 * 60 * 30, // Cache for 30 minutes
  });

  // Fetch featured stories - only once using ref
  const featuredFetchedRef = useRef(false);
  const { data: featuredData } = useInfiniteQuery<StoriesResponse>({
    queryKey: ["featured-stories"],
    queryFn: () => {
      featuredFetchedRef.current = true;
      return StoryAPI.getFeatured();
    },
    initialPageParam: 1,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    getNextPageParam: () => undefined, // Only one page for featured stories
    enabled: !featuredFetchedRef.current, // Only run this query once
  });

  // Intersection observer callback
  const onIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const first = entries[0];
      if (first.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  // Set up intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(onIntersect, {
      threshold: 0.1,
      rootMargin: "200px",
    });

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, [onIntersect]);

  // Reset scroll position when filters change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [search, filtersString, sort]);

  // Add stable keys to stories to help with memoization
  const processStories = useCallback((stories: Story[] = [], prefix: string = "") => {
    return stories.map((story, index) => ({
      ...story,
      key: `${prefix}_${story._id}_${index}`
    }));
  }, []);

  // Prepare stories data with stable identity
  const { allStories, totalStories, featuredStories } = useMemo(() => {
    const processedStories = data?.pages?.flatMap((page, pageIndex) => 
      processStories(page.stories, `page_${pageIndex}`)
    ) ?? [];
    
    return {
      allStories: processedStories,
      totalStories: data?.pages?.[0]?.total ?? 0,
      featuredStories: processStories(featuredData?.pages?.[0]?.stories ?? [], "featured")
    };
  }, [data, featuredData, processStories]);

  // Handlers with stable references
  const handleSearch = useCallback((query: string) => {
    setSearch(query);
  }, []);

  const handleFilter = useCallback((newFilters: FilterOptions) => {
    setFilters(newFilters);
  }, []);

  const handleSort = useCallback((newSort: string) => {
    setSort(newSort);
  }, []);

  return (
    <div className="space-y-8">
      {/* Featured Stories */}
      {featuredStories.length > 0 && (
        <section>
          <h2 className="text-2xl font-semibold mb-4 font-heading">Featured Stories</h2>
          <StoryGrid stories={featuredStories} />
        </section>
      )}

      {/* Three steps - how to find and get a story (matches landing-page style) */}
      <section className="bg-muted/40 rounded-lg border border-border/50 p-4 sm:p-5">
        <p className="text-sm font-medium text-foreground mb-3 sm:mb-4">
          How it works
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-sm text-muted-foreground">
          <div className="flex items-start gap-2">
            <span className="text-primary font-semibold flex-shrink-0">1.</span>
            <span>Search or filter below to find your mystery</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-primary font-semibold flex-shrink-0">2.</span>
            <span>Add to cart and go to checkout</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-primary font-semibold flex-shrink-0">3.</span>
            <span>Download and play with friends</span>
          </div>
        </div>
      </section>

      {/* Search and Filters */}
      <SearchAndFilter
        onSearch={handleSearch}
        onFilter={handleFilter}
        onSort={handleSort}
      />

      {/* All Stories */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold font-heading">All Stories</h2>
          {totalStories > 0 && (
            <p className="text-sm text-muted-foreground">
              {totalStories} stories found
            </p>
          )}
        </div>
        <StoryGrid stories={allStories} loading={isLoading} />
      </section>

      {/* Load More Indicator */}
      {(isFetchingNextPage || hasNextPage) && (
        <div
          ref={loadMoreRef}
          className="py-8 flex items-center justify-center"
        >
          {isFetchingNextPage ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading more stories...
            </div>
          ) : (
            <div className="h-8">
              {/* Spacer for intersection observer */}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(StoriesPage);